import { FC, PropsWithChildren, ReactNode, useEffect } from 'react';
import { css, cx } from '@linaria/atomic';
import { useTranslation } from '@frontend/i18n';
import { IconName } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { Button, ButtonProps, Heading, HeadingProps, Text, TextProps, Truncated } from '@frontend/design-system';
import { ChartEditPanel } from '../chart-edit-panel/chart-edit-panel';
import { useChartContext } from '../chart.provider';
import { useExportChartToPDF, type PDFDetails } from '../hooks/use-export-chart-to-pdf';

interface HeaderProps {
  title?: ReactNode;
  subTitle?: ReactNode;
  action?: ReactNode;
  /** Rendered at the right end of the title row on every screen size, e.g. a navigation shortcut icon. */
  titleAction?: ReactNode;
  containerStyles?: string;
}

export interface HeaderV2Namespace extends FC<HeaderProps> {
  Title: FC<PropsWithChildren<TitleProps>>;
  Action: typeof Action;
  PdfExportAction: typeof PdfExportAction;
  FilterText: typeof FilterText;
  ChartEditPanel: typeof ChartEditPanel;
}

export const HeaderV2: HeaderV2Namespace = ({ title, subTitle, action, titleAction, containerStyles }) => {
  return (
    <div className={cx(styles.headerContainer, containerStyles)}>
      <div className={cx(styles.titleRow, action ? styles.titleRowWithAction : styles.titleRowFullWidth)}>
        {title}
        {titleAction && <div className={styles.titleAction}>{titleAction}</div>}
      </div>
      {action && <div className={styles.actionBar}>{action}</div>}
      <div className={styles.subtitle}>{subTitle}</div>
    </div>
  );
};

HeaderV2.displayName = 'HeaderV2';

interface TitleProps {
  leftElement?: ReactNode;
  rightElement?: ReactNode;
  infoTip?: ReactNode;
  headerLevel?: HeadingProps['level'];
}
const Title: FC<PropsWithChildren<TitleProps>> = ({
  leftElement,
  rightElement,
  infoTip,
  children,
  headerLevel = 3,
}) => {
  return (
    <div className={styles.titleContainer}>
      {leftElement}
      <Heading level={headerLevel} className={styles.title}>
        <Truncated as='span' className={styles.wrappedTitle}>
          {children}
        </Truncated>
        {infoTip}
      </Heading>
      {rightElement}
    </div>
  );
};

export type ChartHeaderAction = ButtonProps & {
  label: string;
  omit?: boolean;
} & (
    | {
        hoverText?: never;
        /** Shows only the icon in small mode and only the label otherwise. */
        responsiveLabel: true;
        iconName: IconName; // required when responsiveLabel is true
        showLabel?: boolean;
      }
    | {
        hoverText?: never;
        responsiveLabel?: false;
        iconName?: IconName;
        showLabel?: boolean;
      }
    | {
        hoverText?: string;
        responsiveLabel?: false;
        iconName?: never;
        showLabel?: never;
      }
  );

const Action: FC<PropsWithChildren<ChartHeaderAction>> = ({
  omit,
  hoverText,
  iconName,
  showLabel,
  responsiveLabel,
  variant = 'tertiary',
  ...rest
}) => {
  const { isSmallMode } = useChartContext();

  if (omit) {
    return null;
  }
  const showIcon = responsiveLabel ? isSmallMode : !!iconName;
  const labelVisible = responsiveLabel ? !isSmallMode : !isSmallMode && showLabel;
  return (
    <Button
      {...rest}
      className='action-button'
      iconName={showIcon ? iconName : undefined}
      key={rest.label}
      size='small'
      variant={variant}
    >
      {labelVisible && rest.label}
    </Button>
  );
};

// TODO: Move this to a separate file
export const PdfExportAction: FC<{ pdfDetails?: PDFDetails; pdfTitle: string }> = ({ pdfDetails = [], pdfTitle }) => {
  const { chartContainer, setShowLoader } = useChartContext();
  const { exportToPDF, isExportingPDF } = useExportChartToPDF(pdfDetails);
  const { t } = useTranslation('analytics');

  useEffect(() => {
    setShowLoader(isExportingPDF);
  }, [isExportingPDF, setShowLoader]);

  return (
    <Action iconName='download' label={t('Export to PDF')} onClick={() => exportToPDF(pdfTitle, chartContainer)} />
  );
};

export const FilterText: FC<{ filterHintText?: string } & TextProps> = ({ filterHintText, ...rest }) => {
  return (
    <Text weight='bold' color='light' size='medium' {...rest}>
      {filterHintText}
    </Text>
  );
};

HeaderV2.Title = Title;
HeaderV2.Action = Action;
HeaderV2.PdfExportAction = PdfExportAction;
HeaderV2.FilterText = FilterText;
HeaderV2.ChartEditPanel = ChartEditPanel;

const styles = {
  headerContainer: css`
    display: grid;
    row-gap: var(--fabric-number-n-1-rem);
    column-gap: var(--fabric-space-gap-md);
    grid-template-columns: minmax(100px, 1fr) auto;
    grid-template-rows: auto;
    grid-template-areas: 'title action' 'subtitle subtitle';
    align-items: center;

    @container (max-width: ${breakpoints.xsmall.max}px) {
      grid-template-columns: minmax(0, 1fr);
      grid-template-areas: 'title' 'subtitle' 'action';
      align-items: start;
      row-gap: var(--fabric-space-gap-sm);
    }
  `,
  titleRow: css`
    display: flex;
    align-items: center;
    row-gap: var(--fabric-number-n-1-rem);
    column-gap: var(--fabric-number-n-1-rem);
    min-width: 100px;
    overflow-x: hidden;
    overflow-y: hidden;

    @container (max-width: ${breakpoints.xsmall.max}px) {
      min-width: 0;
      overflow-x: visible;
      overflow-y: visible;
    }
  `,
  titleRowWithAction: css`
    grid-area: title;
  `,
  titleRowFullWidth: css`
    grid-column: 1 / -1;
    grid-row: 1;

    @container (max-width: ${breakpoints.xsmall.max}px) {
      grid-row: auto;
    }
  `,
  titleAction: css`
    margin-left: auto;
    flex-shrink: 0;
  `,
  actionBar: css`
    width: fit-content;
    flex-shrink: 0;
    grid-area: action;
    justify-self: end;

    @container (max-width: ${breakpoints.xsmall.max}px) {
      justify-self: start;
      max-width: 100%;
    }
  `,
  subtitle: css`
    grid-area: subtitle;
    min-width: 0;
  `,
  titleContainer: css`
    display: flex;
    align-items: center;
    row-gap: var(--fabric-number-n-1-rem);
    column-gap: var(--fabric-number-n-1-rem);
    overflow-x: hidden;
    overflow-y: hidden;

    @container (max-width: ${breakpoints.xsmall.max}px) {
      overflow-x: visible;
      overflow-y: visible;
      min-width: 0;
    }
  `,
  title: css`
    display: flex;
    align-items: center;
    row-gap: var(--fabric-number-n-1-rem);
    column-gap: var(--fabric-number-n-1-rem);
    overflow-x: hidden;
    overflow-y: hidden;
    min-width: 0;

    @container (max-width: ${breakpoints.xsmall.max}px) {
      font-size: var(--fabric-font-size-md);
      overflow-x: visible;
      overflow-y: visible;
    }
  `,
  wrappedTitle: css`
    @container (max-width: ${breakpoints.xsmall.max}px) {
      white-space: normal;
      overflow-x: visible;
      overflow-y: visible;
      text-overflow: clip;
      overflow-wrap: anywhere;
    }
  `,
};
